import React from 'react'
import { chakra, Icon, Text as ChakraText, Link } from '@chakra-ui/react'
import { CgDanger } from 'react-icons/cg'
import { AiOutlineCheckCircle } from 'react-icons/ai'
import numeral from 'numeral'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../../ui-kit'

export const DangerIcon = ({
  hidden,
}: {
  hidden?: boolean
}): React.ReactElement => (
  <Icon
    visibility={hidden ? 'hidden' : 'initial'}
    boxSize="20px"
    as={CgDanger}
    color={colors.appBrick}
  />
)

export const CorrectIcon = ({
  hidden,
}: {
  hidden?: boolean
}): React.ReactElement => (
  <Icon
    visibility={hidden ? 'hidden' : 'initial'}
    boxSize="20px"
    as={AiOutlineCheckCircle}
    color={colors.appGreenishTeal}
  />
)

export const SizeReductionText = ({
  originalFileSize,
  optimizedFileSize,
}: {
  originalFileSize: number
  optimizedFileSize: number
}): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <ChakraText
      textAlign="center"
      color={colors.appBattleshipGrey}
      fontSize="md"
    >
      {t(
        'image_size_can_be_reduced',
        'Image size can be reduced from current ',
      )}
      <chakra.strong>
        {numeral(originalFileSize).format('0.0 ib').replace('i', '')}
      </chakra.strong>
      {` ${t('to', 'to')} `}
      <chakra.strong>
        {numeral(optimizedFileSize).format('0.0 ib').replace('i', '')}
      </chakra.strong>
    </ChakraText>
  )
}

export const LowerQualitySuggestionText = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <ChakraText
      textAlign="center"
      color={colors.appBattleshipGrey}
      fontSize="md"
    >
      {t(
        'consider_loading_the_image',
        'Consider loading the image at a lower quality level.',
      )}
      <Link
        color={colors.appClearBlue}
        href="https://docs.imagekit.io/features/image-optimization/quality-optimization"
        isExternal
      >
        {t('learn_more_performance_suggestion', ' Learn more.')}
      </Link>
    </ChakraText>
  )
}

export const ImageWellOptimizedText = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <CorrectIcon />

      <ChakraText color={colors.appBattleshipGrey} fontSize="md">
        {t('image_well_optimized', 'Image is well optimized')}
      </ChakraText>
    </>
  )
}

export const FormatChangeText = ({
  originalFormat,
  optimizedFormat,
}: {
  originalFormat: string
  optimizedFormat: string
}): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <DangerIcon />

      <ChakraText color={colors.appBattleshipGrey} fontSize="md">
        {`${t('change_format_to', 'Change format to')} `}
        <chakra.strong>{optimizedFormat}</chakra.strong>
        {` ${t('from_current', 'from current')} `}
        <chakra.strong>{originalFormat}</chakra.strong>
        {t('load_image_from_to_fix', '. Load image from ImageKit to fix this.')}
        <Link
          color={colors.appClearBlue}
          href="https://docs.imagekit.io/features/image-optimization/automatic-image-format-conversion"
          isExternal
        >
          {t('learn_more_performance_suggestion', ' Learn more.')}
        </Link>
      </ChakraText>
    </>
  )
}

export const CorrectFormatText = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <CorrectIcon />

      <ChakraText color={colors.appBattleshipGrey} fontSize="md">
        {t(
          'using_appropriate_image_format',
          'Using approppriate image file format',
        )}
      </ChakraText>
    </>
  )
}

export const DimensionResizeText = ({
  originalDimension,
  optimizedDimension,
  optimizedWidth,
  optimizedHeight,
}: {
  originalDimension: string
  optimizedDimension: string
  optimizedWidth: number
  optimizedHeight: number
}): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <DangerIcon />

      <ChakraText color={colors.appBattleshipGrey} fontSize="md">
        {t('correctly_resize_image_to', 'Correctly resize image to ')}
        {optimizedDimension.replace('&#xd7;', '×')}
        {t(
          'as_per_layout_you_are_loading_a',
          ' as per layout. You are loading a ',
        )}
        {originalDimension.replace('&#xd7;', '×')}
        {t('image_add_a_query', ' image. Add a query parameter ')}
        <chakra.strong>{`${t(
          '?tr=w-',
          '?tr=w-',
        )}${optimizedWidth},h-${optimizedHeight}`}</chakra.strong>
        {t('in_the_image_url', ' in the image URL.')}
        <Link
          color={colors.appClearBlue}
          href="https://docs.imagekit.io/features/image-transformations"
          isExternal
        >
          {t(
            'learn_more_from_resizing_examples',
            ' Learn more from resizing examples.',
          )}
        </Link>
      </ChakraText>
    </>
  )
}

export const CorrectResizeText = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      <CorrectIcon />

      <ChakraText color={colors.appBattleshipGrey} fontSize="md">
        {t(
          'image_correctly_resized',
          'Image is correctly resized as per layout',
        )}
      </ChakraText>
    </>
  )
}
